import React, { useEffect, useState, useRef } from "react";
import {
  Bell,
  ChevronDown,
  ChevronUp,
  Calendar,
  Loader2,
  FileText,
} from "lucide-react";
import "../css/notification.css";

const API_URL = "https://auxititpt.org/Backend/notifications_api.php";
const PUBLIC_BASE = "https://localhost";

const Notification = () => {
  const [notifications, setNotifications] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const scrollRef = useRef(null);

  // ✅ Auto-scroll effect
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let scrollInterval;
    let scrollSpeed = 1; // Adjust scroll speed (px per step)

    const startAutoScroll = () => {
      scrollInterval = setInterval(() => {
        if (container.scrollTop + container.clientHeight >= container.scrollHeight) {
          container.scrollTop = 0; // loop to top
        } else {
          container.scrollTop += scrollSpeed;
        }
      }, 100); // Adjust time interval
    };

    startAutoScroll();

    // Pause on hover
    const handleMouseEnter = () => clearInterval(scrollInterval);
    const handleMouseLeave = () => startAutoScroll();

    container.addEventListener("mouseenter", handleMouseEnter);
    container.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      clearInterval(scrollInterval);
      container.removeEventListener("mouseenter", handleMouseEnter);
      container.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [notifications]);

  // Fetch notifications from backend
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await fetch(API_URL);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const filtered = Array.isArray(data)
          ? data.filter((n) => n.status === "active")
          : [];
        setNotifications(filtered);
      } catch (err) {
        console.error("Fetch notifications error:", err);
        setError("Failed to load notifications. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  const toggleExpand = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section className="edu-notification-section py-5">
      <div className="container">
        <div className="notification-header text-center mb-4">
          <Bell className="bell-icon" />
          <h2>Student Notifications</h2>
          <p>Click on a notification title to view full details</p>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Loader2 className="animate-spin text-primary" size={40} />
            <p className="text-muted mt-2">Loading notifications...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger text-center">{error}</div>
        ) : notifications.length === 0 ? (
          <div className="text-center text-muted fs-5">
            No active notifications found.
          </div>
        ) : (
          <div className="notification-scroll-box" ref={scrollRef}>
            <div className="notification-boxes">
              {notifications.map((note, index) => (
                <div
                  key={note.id}
                  className={`notification-box ${
                    activeIndex === index ? "active" : ""
                  }`}
                >
                  <div
                    className="notification-title"
                    onClick={() => toggleExpand(index)}
                  >
                    <div className="title-text">
                      <Calendar size={16} />
                      <span>{note.title}</span>
                    </div>
                    {activeIndex === index ? (
                      <ChevronUp size={20} className="arrow-icon" />
                    ) : (
                      <ChevronDown size={20} className="arrow-icon" />
                    )}
                  </div>

                  {activeIndex === index && (
                    <div className="notification-content">
                      <p className="notification-date">
                        <Calendar size={14} className="me-1" />
                        {new Date(note.created_at).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </p>
                      <p className="notification-details">{note.message}</p>

                      {note.file_url && (
                        <p className="mt-2">
                          <a
                            href={`${PUBLIC_BASE}${note.file_url}`}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                          >
                            <FileText size={16} className="me-2" />
                            View Attachment
                          </a>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Notification;
